import { supabase } from "@/lib/supabase"
import { useEffect, useRef } from 'react'
import {
  collectAnalyticsData,
  getGeolocationDataWithCORSHandling,
  type GeolocationData
} from '@/utils/analytics-utils'

import { useUserPresence } from "use-user-presence";

// Types for analytics
export interface AnalyticsData {
  session_id: string
  fingerprint_hash: string
  ip_address?: string
  user_agent: string
  referer?: string
  device_type: 'mobile' | 'tablet' | 'desktop'
  browser_name?: string
  browser_version?: string
  operating_system?: string
  screen_resolution: string
  country_code?: string
  region?: string
  city?: string
  timezone: string
  language: string
}



// Track preview view
export async function trackPreviewView(previewId: string, analyticsData: AnalyticsData) {
  try {
    // Try to get IP and geolocation data, but don't block if it fails
    let geoData: GeolocationData = {};
    try {
      // Try enhanced geolocation with CORS handling
      geoData = await getGeolocationDataWithCORSHandling();
    } catch (error) {
      console.log('Geolocation detection failed', error);
    }

    const recordData = {
      preview_id: previewId,
      session_id: analyticsData.session_id,
      fingerprint_hash: analyticsData.fingerprint_hash,
      ip_address: geoData.ip || null,
      user_agent: analyticsData.user_agent,
      referer: analyticsData.referer || null,
      device_type: analyticsData.device_type,
      browser_name: analyticsData.browser_name,
      browser_version: analyticsData.browser_version,
      operating_system: analyticsData.operating_system,
      screen_resolution: analyticsData.screen_resolution,
      country_code: geoData.country || null,
      region: geoData.region || null,
      city: geoData.city || null,
      timezone: analyticsData.timezone,
      language: analyticsData.language,
    };

    // First try to insert
    const { error: insertError } = await supabase
      .from('preview_analytics')
      .insert(recordData);

    if (insertError) {
      // If insert failed due to unique constraint, try update
      if (insertError.code === '23505') { // unique_violation
        // First get the current page_views count
        const { data: existingData } = await supabase
          .from('preview_analytics')
          .select('page_views')
          .eq('preview_id', previewId)
          .eq('session_id', analyticsData.session_id)
          .single();

        const { error: updateError } = await supabase
          .from('preview_analytics')
          .update({
            last_seen_at: new Date().toISOString(),
            page_views: (existingData?.page_views || 0) + 1,
            updated_at: new Date().toISOString()
          })
          .eq('preview_id', previewId)
          .eq('session_id', analyticsData.session_id);

        if (updateError) {
          console.error('Error updating preview analytics:', updateError);
        } else {
          console.log('Successfully updated existing analytics session');
        }
      } else {
        console.error('Error inserting preview analytics:', insertError);
      }
    } else {
      console.log('Successfully tracked new preview view with data:', {
        hasIP: !!geoData.ip,
        hasCountry: !!geoData.country,
        hasRegion: !!geoData.region,
        device: analyticsData.device_type,
        browser: analyticsData.browser_name
      });
    }
  } catch (error) {
    console.error('Error tracking preview view:', error)
  }
}

// Update session duration
export async function updateSessionDuration(sessionId: string, durationSeconds: number) {
  try {
    const { error } = await supabase.rpc('update_session_duration', {
      p_session_id: sessionId,
      p_duration_seconds: durationSeconds
    })
    if (error) {
      console.error('Error updating session duration:', error)
    }
    console.log(`Session duration updated: ${durationSeconds}s`)
  } catch (error) {
    console.error('Error updating session duration:', error)
  }
}

// Hook for using analytics in React components
export function useAnalytics(previewId: string) {
  const analyticsDataRef = useRef<AnalyticsData | null>(null)
  const { isPresent } = useUserPresence();
  const activeTimeRef = useRef<number>(0) // Total active time in milliseconds
  const lastActiveStartRef = useRef<number | null>(null) // When current active session started
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null) // Timer for regular updates

  // Initialize analytics data once
  useEffect(() => {
    const setupAnalytics = async () => {
      analyticsDataRef.current = await collectAnalyticsData()
      console.log('🔧 Analytics initialized for preview:', previewId, 'session:', analyticsDataRef.current.session_id)
      await trackPreviewView(previewId, analyticsDataRef.current)
    }
    setupAnalytics();
  }, []);

  useEffect(() => {
    // Track user presence and update session duration
    const handlePresenceChange = async () => {
      if (analyticsDataRef.current?.session_id) {
        const now = Date.now()

        if (isPresent) {
          console.log("User is present in the shared viewer")
          // Start tracking active time
          lastActiveStartRef.current = now
          
          // Start timer for regular updates every 15 seconds
          if (updateTimerRef.current) {
            clearInterval(updateTimerRef.current)
          }
          updateTimerRef.current = setInterval(async () => {
            if (analyticsDataRef.current?.session_id && lastActiveStartRef.current !== null) {
              const currentTime = Date.now()
              const totalActiveDuration = activeTimeRef.current + (currentTime - lastActiveStartRef.current)
              const activeDurationSeconds = Math.floor(totalActiveDuration / 1000)
              await updateSessionDuration(analyticsDataRef.current.session_id, activeDurationSeconds)
            }
          }, 15000) // Update every 15 seconds
        } else {
          console.log("User is not present in the shared viewer")
          // Clear the update timer
          if (updateTimerRef.current) {
            clearInterval(updateTimerRef.current)
            updateTimerRef.current = null
          }
          
          // Add the time spent in this active session to total active time
          if (lastActiveStartRef.current !== null) {
            activeTimeRef.current += now - lastActiveStartRef.current
            lastActiveStartRef.current = null
          }
        }

        // Calculate total active duration
        let totalActiveDuration = activeTimeRef.current
        if (isPresent && lastActiveStartRef.current !== null) {
          // Add current active session time
          totalActiveDuration += now - lastActiveStartRef.current
        }

        const activeDurationSeconds = Math.floor(totalActiveDuration / 1000)
        await updateSessionDuration(analyticsDataRef.current.session_id, activeDurationSeconds)
      }
    }

    handlePresenceChange()
  }, [isPresent])

  // Cleanup: save final active time when component unmounts
  useEffect(() => {
    return () => {
      // Clear the update timer
      if (updateTimerRef.current) {
        clearInterval(updateTimerRef.current)
      }
      
      if (lastActiveStartRef.current !== null) {
        activeTimeRef.current += Date.now() - lastActiveStartRef.current
      }
    }
  }, [])

  return {
    sessionId: analyticsDataRef.current?.session_id
  }
}
